import { forwardRef, InputHTMLAttributes } from 'react';

export interface NumberFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  inputClassName?: string;
}

/**
 * NumberField - Number input with label, error, hint, and formatting support
 */
const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  (
    {
      label,
      error,
      hint,
      required = false,
      id,
      min,
      max,
      step,
      prefix,
      suffix,
      className = '',
      inputClassName = '',
      ...props
    },
    ref
  ) => {
    const fieldId = id || props.name;

    const baseInputStyles =
      'w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-accent-500 bg-white dark:bg-dark-bg text-light-text dark:text-dark-text transition-colors';
    const errorStyles = error
      ? 'border-error focus:ring-error'
      : 'border-light-border dark:border-dark-border';

    // Adjust padding if prefix/suffix present
    const paddingStyles = prefix ? 'pl-10' : suffix ? 'pr-12' : '';

    return (
      <div className={`space-y-1.5 ${className}`}>
        {label && (
          <label
            htmlFor={fieldId}
            className="block text-sm font-medium text-light-text dark:text-dark-text"
          >
            {label}
            {required && <span className="text-error ml-0.5">*</span>}
          </label>
        )}
        <div className="relative">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-light-muted dark:text-dark-muted text-sm">
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            id={fieldId}
            type="number"
            min={min}
            max={max}
            step={step}
            className={`${baseInputStyles} ${errorStyles} ${paddingStyles} ${inputClassName}`}
            aria-invalid={!!error}
            aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
            {...props}
          />
          {suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted dark:text-dark-muted text-sm">
              {suffix}
            </span>
          )}
        </div>
        {error && (
          <p id={`${fieldId}-error`} className="text-xs text-error animate-fade-in" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${fieldId}-hint`} className="text-xs text-light-muted dark:text-dark-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

NumberField.displayName = 'NumberField';

export default NumberField;
